import React from "react"
import SideBarLayout from "../../../layout/side-bar-layout"
import {TITLE_DESIGN_SYSTEM_PAGE} from "../../../../utils/constants"
import {
    Paper,
    Typography,
    Box,
    Stack
} from "@mui/material"
import ButtonWithIcon from "../../../components/Button/Button"
import CodeViewer from "../../../components/CodeViewer/code-viewer"
import {ReactComponent as SearchIcon} from "src/v2/assets/icons/search-icon.svg"

const ButtonsDesignSystemPage = () => {
    const TITLE_PAGE = `${TITLE_DESIGN_SYSTEM_PAGE} | Buttons`

    const codeBasicUsage = `import { ReactComponent as SearchIcon } from "src/v2/assets/icons/search-icon.svg"
import ButtonWithIcon from "src/v2/components/Button/Button"

<ButtonWithIcon icon={<SearchIcon />}>
  Pesquisar
</ButtonWithIcon>`

    const codeVariants = `import { ReactComponent as SearchIcon } from "src/v2/assets/icons/search-icon.svg"
import ButtonWithIcon from "src/v2/components/Button/Button"

<>
  <ButtonWithIcon icon={<SearchIcon />}>
    Padrão
  </ButtonWithIcon>

  <ButtonWithIcon icon={<SearchIcon />} variant="outlined">
    Outlined
  </ButtonWithIcon>

  <ButtonWithIcon icon={<SearchIcon />} color="secondary">
    Secundário
  </ButtonWithIcon>

  <ButtonWithIcon icon={<SearchIcon />} size="lg">
    Grande
  </ButtonWithIcon>

  <ButtonWithIcon icon={<SearchIcon />} disabled>
    Desabilitado
  </ButtonWithIcon>
</>`

    const codePropsTable = `// API do componente ButtonWithIcon
type ButtonWithIconProps = {
  size?: "sm" | "md" | "lg"        // depende das chaves de BUTTON_SIZES
  disabled?: boolean
  icon?: React.ReactNode
  children: React.ReactNode        // label do botão
  // ...props são repassadas para StyledButton (onClick, color, variant, etc.)
}`

    return (
        <SideBarLayout
            header
            title={TITLE_PAGE}
            headerTitle={TITLE_PAGE}
        >
            <Box sx={{mt: -2}}>
                <Stack spacing={3}>
                    {/* INTRO + EXEMPLO PRINCIPAL */}
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h4" gutterBottom color="common.white">
                                {`Buttons`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {`Os botões são usados para acionar ações primárias e secundárias na interface. O componente ButtonWithIcon adiciona suporte a ícones alinhados ao conteúdo, mantendo consistência com o design system.`}
                            </Typography>

                            <Box mt={3}>
                                <Typography variant="h6" gutterBottom color="common.white">
                                    {`Exemplo principal`}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {`Exemplo de botão com ícone à esquerda, ideal para ações como busca, filtros e comandos frequentes.`}
                                </Typography>

                                <Stack direction="row" spacing={2} alignItems="center">
                                    <ButtonWithIcon icon={<SearchIcon/>}>
                                        {`Pesquisar`}
                                    </ButtonWithIcon>
                                </Stack>
                            </Box>
                        </Box>
                    </Paper>

                    {/* VARIAÇÕES DE ESTILO */}
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Variações de estilo`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`O ButtonWithIcon herda as propriedades visuais do botão base (como variant, color, disabled e size). Isso permite que o componente seja reutilizado em diferentes contextos mantendo o mesmo padrão visual.`}
                            </Typography>

                            <Box mb={2}>
                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                    <ButtonWithIcon icon={<SearchIcon/>}>
                                        {`Padrão`}
                                    </ButtonWithIcon>

                                    <ButtonWithIcon icon={<SearchIcon/>} variant="outlined">
                                        {`Outlined`}
                                    </ButtonWithIcon>

                                    <ButtonWithIcon icon={<SearchIcon/>} color="secondary">
                                        {`Secundário`}
                                    </ButtonWithIcon>

                                    <ButtonWithIcon icon={<SearchIcon/>} size="lg">
                                        {`Grande`}
                                    </ButtonWithIcon>

                                    <ButtonWithIcon icon={<SearchIcon/>} disabled>
                                        {`Desabilitado`}
                                    </ButtonWithIcon>
                                </Stack>
                            </Box>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <Typography variant="subtitle2" gutterBottom color="common.white">
                                        {`Código de exemplo`}
                                    </Typography>
                                    <CodeViewer code={codeVariants} language="jsx"/>
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>

                    {/* USO BÁSICO */}
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Uso básico`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Exemplo mínimo de importação e uso do ButtonWithIcon com um ícone SVG.`}
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <CodeViewer code={codeBasicUsage} language="jsx"/>
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>

                    {/* PROPRIEDADES DO COMPONENTE */}
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Propriedades do Componente`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Propriedades principais do componente ButtonWithIcon.`}
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <Typography variant="subtitle2" gutterBottom color="common.white">
                                        {`Tipo das props (exemplo em TypeScript)`}
                                    </Typography>
                                    <CodeViewer code={codePropsTable} language="tsx"/>
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>
                </Stack>
            </Box>
        </SideBarLayout>
    )
}

export default ButtonsDesignSystemPage